import reduce from "lodash/reduce";
import { useExperienceLabelMap } from "PFApp/hooks/use_experience_label_map";
import { useImportanceLabelMap } from "PFApp/hooks/use_importance_label_map";
import { GenericActivityEvent } from "PFTypes";
import { useTranslation } from "react-i18next";

import { sanitizeAvailabilityRange } from "./sanitize_history_entry.utils";

export const useSanitizedAttributes = (
  diff: GenericActivityEvent["diff"],
  metadata: GenericActivityEvent["metadata"],
  label?: string
): GenericActivityEvent["diff"] => {
  const { t } = useTranslation("activities", { keyPrefix: "show.history.entry.updatedAttributes" });
  const experienceLabelMap = useExperienceLabelMap();
  const importanceLabelMap = useImportanceLabelMap();

  return reduce(
    diff,
    (acc, value: object, key) => {
      // Hide metadata key from the UI
      if (key === "metadata") {
        return { ...acc, ...value! };
      }

      // Hide id key from the UI
      if (key === "id") {
        return acc;
      }

      // Flatten the Availability Ranges object with Index
      if (key === "ranges") {
        const flattenedRanges = reduce(
          value,
          (acc, value, key) => ({
            ...acc,
            [t("range", { index: key })]: sanitizeAvailabilityRange(
              value,
              Number(key) - 1,
              metadata.availability
            )
          }),
          {}
        );

        return { ...acc, ...flattenedRanges };
      }

      if (key === "private") {
        const privacyValues = (value as boolean[]).map((val) => t(val ? "private.true" : "private.false"));
        return { ...acc, [key]: privacyValues };
      }

      if (label === "activity_tag") {
        return [diff[0] || "-", diff[1] || "-"];
      }

      // Translate the experience number values to readable labels
      if (label === "requiredExperience") {
        return [experienceLabelMap[diff[0]], experienceLabelMap[diff[1]]];
      }

      // Translate the importance number values to readable labels
      if (label === "importance") {
        return [importanceLabelMap[diff[0]], importanceLabelMap[diff[1]]];
      }

      return { ...acc, [key]: value };
    },
    {}
  );
};
